/**
 * Bảng giá đất TP Hà Nội áp dụng từ 01/01/2026 (Nghị quyết 52/2025/NQ-HĐND): pure helpers shared by
 * the "Giá đất" tab, the table dialog, the map layer and the self-test. Data: public/data/gia/
 * (scripts/gia_parse.py → gia_match.py → gia_export.py):
 *   index.json  {areas:[{a,w,rural,n}], agri:[{a,w,z:[[vùng, lúa, lâu năm, thuỷ sản, rừng]]}], wards:[{n,a,b,g}]}
 *   a<N>.json   {a, rural, rows:[[tt, nhóm, tên, từ | mô tả, đến, giá[12], g]], lines:[[[row…], [lng,lat,…]]]}
 * giá[12] (nghìn đồng/m²) = đất ở VT1–4, đất TMDV VT1–4, đất SXKD phi nông nghiệp VT1–4;
 * g: 0 chưa có vị trí, 1 đoạn đã định vị, 2 đường đã định vị nhưng chưa tách được đoạn,
 *    3 đường trong khu đô thị / khu tái định cư (theo mặt cắt), 4 khu dân cư nông thôn.
 */
import { fold } from './text.js';

export const LAND = [['o', 'Đất ở'], ['tm', 'Đất thương mại, dịch vụ'], ['sx', 'Đất sản xuất, kinh doanh phi nông nghiệp']];
/** VT1 đất ở classes (nghìn đ/m²) used to colour the map layer. */
export const CLASS_STEPS = [10000, 20000, 40000, 70000, 120000, 200000, 300000];
export const CLASS_COLORS = ['#3b82c4', '#3fa7b5', '#6cc49a', '#b5d96b', '#f6d04d', '#f59e3a', '#e8572e', '#b3132b'];
export const classOf = (v) => { let i = 0; while (i < CLASS_STEPS.length && v >= CLASS_STEPS[i]) i++; return i; };

export function rowOf(area, i) {
  const r = area.rows[i];
  if (!r) return null;
  const [tt, sec, s, f, t, p, g] = r;
  return { a: area.a, i, tt, sec, s, f, t, p, g, rural: area.rural };
}

/** Title / segment text of a row as the table prints it. */
export function rowTitle(r) {
  if (r.rural) return r.f ? r.f : r.s;
  return r.s;
}
export function rowSegment(r) {
  if (r.rural) return r.f ? r.s : '';
  if (r.g === 3) return r.f ? `Mặt cắt ${r.f}` : '';
  if (!r.f && !r.t) return '';
  if (/^đầu đường$/i.test(r.f) && /^cuối đường$/i.test(r.t)) return 'Cả tuyến (đầu – cuối đường)';
  return `${r.f || '…'} → ${r.t || '…'}`;
}

/** Price in million đồng / m² from thousand đồng / m² ("702,2"), '—' when absent. */
export function trieu(v, nf = (x, d) => x.toFixed(d).replace('.', ',')) {
  if (v == null || !Number.isFinite(+v)) return '—';
  const m = v / 1000;
  return nf(m, m >= 100 ? 1 : 2);
}

/* ---------------- geometry ---------------- */

const KY = 110574;
const kx = (lat) => 111320 * Math.cos((lat * Math.PI) / 180);

function inRing(x, y, ring) {
  let ins = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) ins = !ins;
  }
  return ins;
}

/** Ward (phường / xã, 2025 boundaries; g = outer rings) containing the point, or null. */
export function wardAt(index, lat, lng) {
  let best = null, bestA = Infinity;
  for (const w of index.wards) {
    const [a, b, c, d] = w.b;
    if (lng < a || lng > c || lat < b || lat > d) continue;
    // an enclave lies inside its neighbour's outer ring too: the smaller ward wins
    if (w.g.some((ring) => inRing(lng, lat, ring)) && (c - a) * (d - b) < bestA) { best = w; bestA = (c - a) * (d - b); }
  }
  return best;
}

/** Areas (khu vực) worth loading around a point: its ward's, plus those of wards within ~200 m. */
export function areasNear(index, lat, lng, pad = 0.002) {
  const out = new Set();
  const w = wardAt(index, lat, lng);
  if (w) out.add(w.a);
  for (const x of index.wards) {
    const [a, b, c, d] = x.b;
    if (lng >= a - pad && lng <= c + pad && lat >= b - pad && lat <= d + pad) out.add(x.a);
  }
  return [...out];
}

/** Adds per-line bounding boxes (once) so lookups can skip far lines. */
export function prepArea(area) {
  if (area.bb) return area;
  area.bb = area.lines.map(([, c]) => {
    let w = 180, s = 90, e = -180, n = -90;
    for (let k = 0; k < c.length; k += 2) { const x = c[k], y = c[k + 1]; if (x < w) w = x; if (x > e) e = x; if (y < s) s = y; if (y > n) n = y; }
    return [w, s, e, n];
  });
  return area;
}

/** Distance (m) from the point to a flat [lng,lat,…] polyline. */
export function distToLine(lat, lng, c) {
  const fx = kx(lat);
  let best = Infinity;
  let px = (c[0] - lng) * fx, py = (c[1] - lat) * KY;
  if (c.length === 2) return Math.hypot(px, py);
  for (let k = 2; k < c.length; k += 2) {
    const qx = (c[k] - lng) * fx, qy = (c[k + 1] - lat) * KY;
    const dx = qx - px, dy = qy - py, L = dx * dx + dy * dy;
    let t = L ? -(px * dx + py * dy) / L : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const d = Math.hypot(px + t * dx, py + t * dy);
    if (d < best) best = d;
    px = qx; py = qy;
  }
  return best;
}

/**
 * Priced rows whose estimated road lines pass within `maxD` metres, nearest first:
 * [{a, i, d, amb}] (amb = the line could not be split between the street's segments).
 */
export function nearestRows(areas, lat, lng, maxD = 200) {
  const pad = maxD / KY * 1.6;
  const best = new Map();
  for (const area of areas) {
    prepArea(area);
    area.lines.forEach(([ids, c], li) => {
      const [w, s, e, n] = area.bb[li];
      if (lng < w - pad || lng > e + pad || lat < s - pad || lat > n + pad) return;
      const d = distToLine(lat, lng, c);
      if (d > maxD) return;
      for (const i of ids) {
        const k = area.a + ':' + i;
        const o = best.get(k);
        if (!o || d < o.d) best.set(k, { a: area.a, i, d, amb: ids.length > 1 });
      }
    });
  }
  return [...best.values()].sort((x, y) => x.d - y.d);
}

/** Lines of one row as a GeoJSON MultiLineString (null when the row has no estimated position). */
export function rowGeometry(area, i) {
  const out = [];
  for (const [ids, c] of area.lines) {
    if (!ids.includes(i)) continue;
    const l = [];
    for (let k = 0; k < c.length; k += 2) l.push([c[k], c[k + 1]]);
    out.push(l);
  }
  return out.length ? { type: 'MultiLineString', coordinates: out } : null;
}

export function geomBBox(g) {
  let w = 180, s = 90, e = -180, n = -90;
  for (const l of g.coordinates) for (const [x, y] of l) { if (x < w) w = x; if (x > e) e = x; if (y < s) s = y; if (y > n) n = y; }
  return [w, s, e, n];
}

/* ---------------- text ---------------- */

/** Bare ward name ("Phường Hoàn Kiếm" → "Hoàn Kiếm"). */
export const wardName = (n) => String(n || '').replace(/^(phường|xã)\s+/i, '');

/** Does the text name this (current, 2025) ward — "xã Thư Lâm" yes, "xã Thư Lâm cũ" no? */
export function mentions(text, ward) {
  const t = ' ' + fold(text) + ' ', w = fold(wardName(ward));
  if (!w) return false;
  let k = t.indexOf(' ' + w + ' ');
  while (k >= 0) {
    if (!t.startsWith(' cu ', k + w.length + 1)) return true;
    k = t.indexOf(' ' + w + ' ', k + 1);
  }
  return false;
}

/** Agricultural land price group (đ/m²) of a ward. */
export function agriOf(index, ward) {
  if (!ward) return null;
  const w = fold(wardName(ward.n));
  const gs = index.agri.filter((g) => g.a === ward.a);
  return gs.find((g) => fold(g.w).split(/\s*,\s*|\s+(?:xa|phuong)\s+/).some((x) => x.replace(/^(cac )?(xa|phuong) /, '') === w)) || gs[0] || null;
}

/** Rows matching a search (all words, accent-insensitive) in title / segment text. */
export function searchRows(area, q) {
  const words = fold(q).split(' ').filter(Boolean);
  const out = [];
  for (let i = 0; i < area.rows.length; i++) {
    const r = rowOf(area, i);
    if (!words.length) { out.push(r); continue; }
    const h = fold(`${r.sec} ${r.s} ${r.f} ${r.t}`);
    if (words.every((x) => h.includes(x))) out.push(r);
  }
  return out;
}
